import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import STATUS from 'constants/status';
import { OpDivider } from 'components/customAntd/DLS/OpDivider/OpDivider';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { getStatusColor, profileIcon } from 'utils/visitorsHelper';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { formatFullName, hasPermission } from 'utils/utils';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { getRequest } from 'api/apiClient';
import { ConfirmDeniedData, DenyReason } from 'types/denyTypes';
import { useTranslation } from 'react-i18next';

interface VisitorsModalProps {
    open: boolean;
    newStatus: number;
    visitorList: any[];
    onConfirm: (data: ConfirmDeniedData) => void;
    onClose: () => void;
}

const SignInManuallyAccessControlModal: React.FC<VisitorsModalProps> = ({ open, newStatus, visitorList, onConfirm, onClose }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const [denyReasons, setDenyReasons] = useState<DenyReason[]>([]);
    const [addToBlockList, setAddToBlockList] = useState(false);
    const [addToWatchList, setAddToWatchList] = useState(false);

    const [form] = OpForm.useForm();

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    const initialValues = { reasonForDenial: denyReasons[0]?.id };

    const hasRedFlagWrite = hasPermission(tokenScopeList, orgId, 'o', 'redflag:w');
    const hasVisitorlogWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitorlog:w');
    const hasDenyRead = hasPermission(tokenScopeList, orgId, 'o', 'deny:r');

    useEffect(() => {
        const fetchData = async () => {
            // Fetch deny reasons only if user has deny:r permission
            if (!hasDenyRead) return;

            const denyReasonPriority = [
                t('visitors.accessControlModal.denyReasons.insufficientId'),
                t('visitors.accessControlModal.denyReasons.blockWatchList'),
                t('visitors.accessControlModal.denyReasons.byHostTeam'),
                t('visitors.accessControlModal.denyReasons.appointmentIssues'),
                t('visitors.accessControlModal.denyReasons.behaviorIssues'),
                t('visitors.accessControlModal.denyReasons.safetyConcerns'),
                t('visitors.accessControlModal.denyReasons.other')
            ];
            try {
                const denyReasons = await getRequest(`/orgs/${orgId}/denyReason`);
                const orderedDenyReasons = denyReasons.data.sort(
                    (a: { name: string }, b: { name: string }) => denyReasonPriority.indexOf(a.name || '') - denyReasonPriority.indexOf(b.name || '')
                );
                setDenyReasons(orderedDenyReasons);
            } catch (error) {
                console.log("Failed to fetch deny reasons.");
            }
        };
        fetchData();
    }, [orgId, dispatch, t, hasDenyRead]);


    useEffect(() => {
        if (open && visitorList.length > 0) {
            const checkedIds = visitorList
                .filter((visitor) => visitor.status === STATUS.DENIED_ENTRY.id)
                .map((visitor) => String(visitor.id));

            const deniedVisitor = visitorList.find((visitor) => visitor.status === STATUS.DENIED_ENTRY.id);

            form.setFieldsValue({
                visitorsCheckbox: checkedIds,
                reasonForDenial: deniedVisitor?.reasonId || denyReasons[0].id,
                detail: deniedVisitor?.details || '',
                addToBlockList: deniedVisitor?.addToBlockList || false,
                addToWatchList: deniedVisitor?.addToWatchList || false,
            });
        }
    }, [open, visitorList, denyReasons, form])

    const visitorOptions = visitorList.map((visitor) => ({
        label: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
        value: `${visitor.id}`,
        firstName: visitor.firstName,
        lastName: visitor.lastName,
        color: visitor?.status === STATUS.DENIED_ENTRY.id ? getStatusColor("Denied Entry") : getStatusColor("Signed In"),
    }));

    const onOkHandler = async () => {
        const selectedVisitors = form.getFieldValue('visitorsCheckbox') || [];
        const reasonId = form.getFieldValue('reasonForDenial');
        const details = form.getFieldValue('detail');
        const addToBlockList = form.getFieldValue('addToBlockList');
        const addToWatchList = form.getFieldValue('addToWatchList');

        onConfirm({ selectedVisitors, reasonId, details, addToBlockList, addToWatchList });
        onClose();
    };

    const getTitle = () => {
        switch (newStatus) {
            case Number(STATUS.SIGNED_IN.id):
                return t('visitors.accessControlModal.title.signIn');
            case Number(STATUS.SIGNED_OUT.id):
                return t('visitors.accessControlModal.title.signOut');
            case Number(STATUS.DENIED_ENTRY.id):
                return t('signInManuallyDrawer.footer.denyEntry');
            default:
                return '';
        }
    };

    const handleBlockListChange = (checked: boolean) => {
        setAddToBlockList(checked);
        if (checked) {
            setAddToWatchList(false);
            form.setFieldsValue({ addToWatchList: false });
        }
    };

    const handleWatchListChange = (checked: boolean) => {
        setAddToWatchList(checked);
        if (checked) {
            setAddToBlockList(false);
            form.setFieldsValue({ addToBlockList: false });
        }
    };

    const finalOnCancel = () => {
        if (form.isFieldsTouched()) {
            confirmModal({
                title: t('confirmDiscard.title'),
                content: t('confirmDiscard.message'),
                okText: t('confirmDiscard.ok'),
                cancelText: t('confirmDiscard.cancel'),
                onOk: () => {
                    form.resetFields();
                    setAddToBlockList(false);
                    setAddToWatchList(false);
                    onClose();
                },
            });
        } else {
            setAddToBlockList(false);
            setAddToWatchList(false);
            onClose();
        }
    };

    return (
        <>
            <OpModal
                title={getTitle()}
                open={open}
                onOk={onOkHandler}
                onCancel={finalOnCancel}
                centered
            >
                <OpDivider />

                <OpForm
                    form={form}
                    initialValues={initialValues}
                    // onSubmit={}
                    hasError={false}
                    defaultButtons={false} onSubmit={function (args: IOnSubmitArgs<any>): void {
                        throw new Error('Function not implemented.');
                    }}                >
                    <OpForm.CheckboxGroup name="visitorsCheckbox" >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            {visitorOptions?.map(option => (
                                <OpForm.Checkbox key={option.value} value={option.value} style={{ marginRight: '8px' }}>
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {profileIcon({ firstName: option.firstName || "", lastName: option.lastName || "", size: 28, color: option.color })}
                                        <span style={{ marginLeft: '8px' }}>{option.label}</span>
                                    </span>
                                </OpForm.Checkbox>
                            ))}
                        </div>
                    </OpForm.CheckboxGroup>

                    {newStatus === Number(STATUS.DENIED_ENTRY.id) && (
                        <>
                            <OpForm.Item label={t('visitors.accessControlModal.reasonForDenial')} name="reasonForDenial">
                                <Select
                                    options={denyReasons.map(reason => ({
                                        label: t(reason.languageCode ?? '', reason.name ?? ''),
                                        value: reason.id
                                    }))}
                                />
                            </OpForm.Item>
                            {(hasVisitorlogWrite) && (
                                <OpForm.TextAreaInput label={t('visitors.accessControlModal.details')} name="detail" rows={4} />
                            )}

                            {hasRedFlagWrite && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <OpForm.Switch name="addToBlockList" style={{ marginBottom: 0 }} checked={addToBlockList} onChange={handleBlockListChange} />
                                        <span style={{ marginLeft: 8 }}>{t('visitors.accessControlModal.addToBlockList')}</span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <OpForm.Switch name="addToWatchList" style={{ marginBottom: 0 }} checked={addToWatchList} onChange={handleWatchListChange} />
                                        <span style={{ marginLeft: 8 }}>{t('visitors.accessControlModal.addToWatchList')}</span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </OpForm>
            </OpModal>

        </>
    );
};

export default SignInManuallyAccessControlModal;